import { useQueryClient } from "react-query";
import { axiosInstance } from "api/axios";
import useCustomQuery from "hooks/useCustomQuery";
import useCustomMutation from "hooks/useCustomMutation";
import { IKey } from "dto/IKey";

/**
 * Akceptacja i Podpis — REST API (BE: AcceptanceController). Skrzynki
 * (Otrzymane / Powiadomienia / Wysłane) oraz wykonany timeline akceptacji
 * dokumentów (do metryki). Zastępuje dane mock na FE.
 */

// ---------------------------------------------------------------------------
// Timeline akceptacji
// ---------------------------------------------------------------------------

export type ApprovalAction = "submit" | "approve" | "reject" | "send_back";

export interface IApprovalEvent {
  who: string;
  when: string;
  action: ApprovalAction;
  stepName?: string;
}

interface ApprovalEventApi {
  Who?: string;
  When: string;
  Action: ApprovalAction;
  StepName?: string;
}

/** Typ dokumentu -> prefiks trasy modułu (jak w AcceptanceController). */
const TIMELINE_PATH: Record<string, string> = {
  engagement: "/budget/engagement",
  expense: "/budget/expense",
  contract: "/contracts",
  invoice: "/registry/invoices",
  case: "/cases",
};

const getApprovalTimelineApi = async (
  documentType: string,
  id: string,
): Promise<IApprovalEvent[]> => {
  const prefix = TIMELINE_PATH[documentType];
  if (!prefix || !id) return [];
  const { data } = await axiosInstance.get<ApprovalEventApi[]>(
    `${prefix}/${id}/approval-timeline`,
  );
  return (data ?? []).map((e) => ({
    who: e.Who ?? "",
    when: e.When,
    action: e.Action,
    stepName: e.StepName,
  }));
};

// ---------------------------------------------------------------------------
// Skrzynki „Akceptacja i Podpis"
// ---------------------------------------------------------------------------

export interface IAcceptanceSignItem {
  Id: string;
  DocumentType: string;
  DocumentId: string;
  Title?: string;
  Subject?: string;
  Action?: "accept" | "sign" | "complete";
  WorkflowId?: string;
  StepId?: string;
  /** Przydział etapu silnika workflow — do decyzji z pozycji. */
  AssignmentId?: string | null;
  /** Instancja workflow — do odczytu stanu obiegu. */
  InstanceId?: string | null;
  Sender?: string;
  Status:
    | "Pending"
    | "Accepted"
    | "Signed"
    | "Rejected"
    | "Returned"
    | "Closed";
  CreatedAt: string;
  ReceivedAt?: string;
  /** Plik do podglądu w panelu prawym (skan/dokument przekazany do akceptacji). */
  FileUri?: string;
  EncryptionKeyId?: string;
  FileName?: string;
  /** Zawinięte klucze obiektu pliku — do KeysContext, by odszyfrować podgląd. */
  ObjectKeys?: IKey[];
}

export const getBoxApi = async (
  box: "received" | "notifications" | "sent",
): Promise<IAcceptanceSignItem[]> =>
  (await axiosInstance.get<IAcceptanceSignItem[]>(`/acceptance-sign/${box}`)).data ?? [];

export const getReceivedApi = () => getBoxApi("received");
export const getNotificationsApi = () => getBoxApi("notifications");
export const getSentApi = () => getBoxApi("sent");

// ---------------------------------------------------------------------------
// Ręczne przekazanie pliku/encji do akceptacji/podpisu lub do wiadomości
// ---------------------------------------------------------------------------

/**
 * Ręczne (poza workflow) przekazanie pliku/encji członkowi zespołu. BE tworzy
 * pozycję w skrzynce odbiorcy (`received` → „Otrzymane", `notifications` →
 * „Do wiadomości") ORAZ re‑wrappuje klucz obiektu pliku dla klucza publicznego
 * odbiorcy (jak współdzielenie teczek), aby odbiorca mógł odszyfrować plik.
 */
export interface IAcceptanceTransferRequest {
  /** UserId odbiorcy (członek workspace). */
  RecipientUserId: string;
  /** Skrzynka docelowa u odbiorcy. */
  Box: "received" | "notifications";
  /** Tryb dla „Otrzymane": akceptacja vs podpis (pomijane dla „Do wiadomości"). */
  Action?: "accept" | "sign";
  /** Typ przekazywanego obiektu (np. „file", „package", „dossier", „drive"). */
  DocumentType: string;
  DocumentId?: string;
  /** Plik (Files) — Uri + klucz obiektu do re‑wrap dla odbiorcy. */
  FileUri?: string;
  EncryptionKeyId?: string;
  FileName?: string;
  /** Tytuł/opis pozycji widoczny u odbiorcy. */
  Title?: string;
  Note?: string;
}

export const transferToAcceptanceApi = (
  payload: IAcceptanceTransferRequest,
): Promise<{ data: IAcceptanceSignItem }> =>
  axiosInstance.post("/acceptance-sign/transfer", payload);

// ---------------------------------------------------------------------------
// Hooki React Query
// ---------------------------------------------------------------------------

const acceptanceService = {
  useGetApprovalTimeline: (documentType: string, id?: string) =>
    useCustomQuery<IApprovalEvent[]>(
      ["approvalTimeline", documentType, id ?? ""],
      () => getApprovalTimelineApi(documentType, id ?? ""),
      { retry: false, enabled: !!id && !!TIMELINE_PATH[documentType] },
    ),

  useGetReceived: () =>
    useCustomQuery<IAcceptanceSignItem[]>(
      ["acceptanceSign", "received"],
      () => getBoxApi("received"),
      { retry: false },
    ),

  useGetNotifications: () =>
    useCustomQuery<IAcceptanceSignItem[]>(
      ["acceptanceSign", "notifications"],
      () => getBoxApi("notifications"),
      { retry: false },
    ),

  useGetSent: () =>
    useCustomQuery<IAcceptanceSignItem[]>(
      ["acceptanceSign", "sent"],
      () => getBoxApi("sent"),
      { retry: false },
    ),

  useTransfer: () => {
    const queryClient = useQueryClient();
    return useCustomMutation(transferToAcceptanceApi, {
      // Po przekazaniu odśwież skrzynki (nadawca zobaczy pozycję w „Wysłane",
      // odbiorca w „Otrzymane"/„Do wiadomości").
      onSuccess: () => {
        queryClient.invalidateQueries(["acceptanceSign"]);
      },
    });
  },
};

export default acceptanceService;
